// src/components/chat/styles/fileUploadStyles.ts

import { CSSProperties } from "react";

/**
 * Styles for the file upload carousel component
 * Maintains consistency with the existing PromptSuggestionCarousel design
 */
export const fileUploadStyles = {
  // Main carousel container - wraps everything including navigation buttons
  carouselContainer: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    marginBottom: "2px",
    position: "relative" as const,
    width: "100%",
  },

  // Scrollable container that holds the file items
  scrollContainer: {
    display: "flex",
    flexDirection: "row" as const,
    overflowX: "auto" as const,
    overflowY: "hidden" as const, // Prevent vertical scrolling
    scrollbarWidth: "none" as any, // Hide scrollbar in Firefox
    msOverflowStyle: "none" as any, // Hide scrollbar in IE/Edge
    WebkitScrollbar: { display: "none" }, // Hide scrollbar in WebKit browsers
    width: "100%",
    padding: "4px 0",
    gap: "8px",
    // Ensure single row layout
    alignItems: "center",
    whiteSpace: "nowrap" as const,
  },

  // Individual file item container
  fileItem: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    backgroundColor: "#F5F5F5",
    borderRadius: "12px",
    padding: "8px 12px",
    minWidth: "max-content",
    position: "relative" as const,
    gap: "8px",
    flexShrink: 0,
    border: "1px solid #E0E0E0",
    transition: "all 0.2s ease",
  },

  // File item in loading state
  fileItemLoading: {
    backgroundColor: "#F8F8F8",
    opacity: 0.8,
  },

  // File icon container
  fileIconContainer: {
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden", // Clip any content that exceeds the container
    position: "relative" as const,
  },

  // File name text
  fileName: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#333",
    fontFamily: "'Segoe UI', sans-serif",
    lineHeight: "18px",
    maxWidth: "150px", // Prevents extremely long names
  },

  // Remove button (X)
  removeButton: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    marginLeft: "4px",
    transition: "all 0.2s ease",
    color: "#666",
    flexShrink: 0,
  },

  // Remove button hover state
  removeButtonHover: {
    backgroundColor: "#E0E0E0",
    color: "#333",
  },

  // Navigation button base style (for scroll arrows)
  navButtonBase: {
    position: "absolute" as const,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    backgroundColor: "white",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
    zIndex: 1,
    cursor: "pointer",
    transition: "opacity 0.2s ease",
    border: "none",
    outline: "none",
  },

  // Previous button positioning
  prevButton: {
    left: 0,
  },

  // Next button positioning
  nextButton: {
    right: 0,
  },

  // Hidden state for navigation buttons
  navButtonHidden: {
    opacity: 0,
    visibility: "hidden" as const,
  },

  // Visible state for navigation buttons
  navButtonVisible: {
    opacity: 0.7,
    visibility: "visible" as const,
  },

  // Hover state for navigation buttons
  navButtonHover: {
    opacity: 1,
  },

  // Empty state message
  emptyStateMessage: {
    fontSize: "13px",
    color: "#999",
    fontStyle: "italic" as const,
    padding: "8px 16px",
    fontFamily: "'Segoe UI', sans-serif",
  },

  // Error message style
  errorMessage: {
    fontSize: "13px",
    color: "#D83B01",
    backgroundColor: "#FDE7E9",
    padding: "8px 12px",
    borderRadius: "8px",
    marginBottom: "8px",
    fontFamily: "'Segoe UI', sans-serif",
  },
};

/**
 * Animation keyframes for file upload components
 * Currently empty - add animations here as needed
 */
export const fileUploadAnimations = ``;

/**
 * Helper function to merge styles conditionally
 * Useful for applying hover or conditional styles
 */
export function mergeStyles(...styles: (CSSProperties | false | undefined)[]): CSSProperties {
  return Object.assign({}, ...styles.filter(Boolean)) as CSSProperties;
}
